import { useEffect, useState, useRef } from "react";
import useCheerStore from "../../store/cheerStore";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon, FolderOpenIcon, TrashIcon,
  MagnifyingGlassIcon, Squares2X2Icon, ListBulletIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { StarIcon as StarOutline } from "@heroicons/react/24/outline";
import TopBar from "../../components/layout/TopBar";
import { Card, CardBody } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import Input from "../../components/ui/Input";
import Badge from "../../components/ui/Badge";
import useProjectStore from "../../store/projectStore";
import useAuthStore from "../../store/authStore";
import { projectsApi } from "../../api/projects";
import { useLookups } from "../../hooks/useLookups";

function useLocalStorage(key, fallback) {
  const [val, setVal] = useState(() => {
    try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
  });
  const set = (v) => { setVal(v); try { localStorage.setItem(key, v); } catch {} };
  return [val, set];
}

export default function ProjectListPage() {
  const { projects, fetchProjects, createProject, deleteProject, loading } = useProjectStore();
  const { user, togglePin } = useAuthStore();
  const triggerCheer = useCheerStore((s) => s.trigger);
  const pinnedIds = new Set(user?.pinned_project_ids || []);
  const { types: machineTypes, colorFor, labelFor } = useLookups("machine_type");

  const [showCreate, setShowCreate]   = useState(false);
  const [form, setForm]               = useState({ name: "", description: "", machine_type_id: null });
  const [creating, setCreating]       = useState(false);
  const [searchName, setSearchName]   = useState("");
  const [searchUser, setSearchUser]   = useState("");
  const [typeFilter, setTypeFilter]   = useState([]);  // [] = All

  const toggleTypeFilter = (t) =>
    setTypeFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching]     = useState(false);
  const [view, setView]               = useLocalStorage("projects_view", "grid");
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!searchName && !searchUser) { setSearchResults(null); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await projectsApi.search({ q: searchName, username: searchUser });
        setSearchResults(data.projects ?? data);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
  }, [searchName, searchUser]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const p = await createProject(form);
      setShowCreate(false);
      setForm({ name: "", description: "", machine_type_id: null });
      navigate(`/projects/${p.id}`);
      triggerCheer("new_project");
    } finally { setCreating(false); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project and all its designs?")) return;
    await deleteProject(id);
  };

  const handlePin = (e, id) => {
    e.stopPropagation();
    togglePin(id);
  };

  const isSearching     = searchName || searchUser;
  const baseProjects    = isSearching ? (searchResults ?? []) : projects;
  const typeFiltered    = typeFilter.length === 0
    ? baseProjects
    : baseProjects.filter(p => typeFilter.includes(p.machine_type_id));

  // Pinned first, then rest — each group keeps server order
  const displayProjects = [
    ...typeFiltered.filter(p => pinnedIds.has(p.id)),
    ...typeFiltered.filter(p => !pinnedIds.has(p.id)),
  ];

  const displayLoading  = isSearching ? searching : loading;

  // ── Pin button ─────────────────────────────────────────────────────
  function PinButton({ id, className = "" }) {
    const pinned = pinnedIds.has(id);
    return (
      <button
        onClick={e => handlePin(e, id)}
        title={pinned ? "Unpin project" : "Pin to top"}
        className={`p-1 rounded transition-colors ${pinned
          ? "text-amber-400 hover:text-amber-500"
          : "text-gray-300 hover:text-amber-400 opacity-0 group-hover:opacity-100"} ${className}`}
      >
        {pinned ? <StarSolid className="h-4 w-4" /> : <StarOutline className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <>
      <TopBar title="Projects" actions={
        <Button icon={PlusIcon} onClick={() => setShowCreate(true)}>New Project</Button>
      } />
      <main className="flex-1 overflow-y-auto p-6">

        {/* ── Toolbar ── */}
        <div className="mb-5 space-y-2">
          {/* Row 1: search inputs + view toggle */}
          <div className="flex gap-3 items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input type="text" value={searchName} onChange={e => setSearchName(e.target.value)}
                placeholder="Search by project name…"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-bh-500" />
            </div>
            <div className="relative w-44">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input type="text" value={searchUser} onChange={e => setSearchUser(e.target.value)}
                placeholder="Filter by user…"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-bh-500" />
            </div>
            <div className="flex rounded-md border border-gray-300 overflow-hidden">
              <button onClick={() => setView("grid")} title="Card view"
                className={`p-2 transition-colors ${view === "grid" ? "bg-bh-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>
                <Squares2X2Icon className="h-4 w-4" />
              </button>
              <button onClick={() => setView("list")} title="List view"
                className={`p-2 border-l border-gray-300 transition-colors ${view === "list" ? "bg-bh-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>
                <ListBulletIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          {/* Row 2: type filter chips */}
          <div className="flex gap-1.5">
            <button onClick={() => setTypeFilter([])}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors
                ${typeFilter.length === 0
                  ? "bg-bh-600 text-white border-bh-600"
                  : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
              All
            </button>
            {machineTypes.map(t => (
              <button key={t.id} onClick={() => toggleTypeFilter(t.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors
                  ${typeFilter.includes(t.id)
                    ? "bg-bh-600 text-white border-bh-600"
                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content ── */}
        {displayLoading ? (
          <p className="text-gray-400 text-sm">Loading…</p>
        ) : !isSearching && displayProjects.length === 0 && typeFilter.length === 0 ? (
          <Card>
            <CardBody className="text-center py-16">
              <FolderOpenIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No projects yet. Create one to get started.</p>
              <Button icon={PlusIcon} onClick={() => setShowCreate(true)}>Create Project</Button>
            </CardBody>
          </Card>
        ) : displayProjects.length === 0 ? (
          <p className="text-gray-400 text-sm">No projects match your filters.</p>
        ) : view === "grid" ? (

          /* ── Grid view ── */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayProjects.map((p) => (
              <Card key={p.id}
                className="cursor-pointer hover:border-bh-300 transition-colors group"
                onClick={() => navigate(`/projects/${p.id}`)}>
                <CardBody>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Badge color={colorFor(p.machine_type_id)}>{labelFor(p.machine_type_id)}</Badge>
                      {pinnedIds.has(p.id) && (
                        <StarSolid className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <PinButton id={p.id} />
                      {!isSearching && p.owner_id === user?.id && (
                        <button onClick={e => handleDelete(e, p.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-red-400 transition-all">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="font-semibold text-gray-900 mt-1">{p.name}</p>
                  {p.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{p.description}</p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-gray-400">
                      Updated {new Date(p.updated_at).toLocaleDateString()}
                    </p>
                    {p.owner_username && (
                      <p className="text-xs text-gray-400">{p.owner_full_name || p.owner_username}</p>
                    )}
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>

        ) : (

          /* ── List view ── */
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="w-8 border-b border-gray-200" />
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Type</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Description</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-36 hidden lg:table-cell">Owner</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-32">Updated</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayProjects.map((p, idx) => {
                  const isPinned = pinnedIds.has(p.id);
                  const prevPinned = idx > 0 && pinnedIds.has(displayProjects[idx - 1].id);
                  const showDivider = !isPinned && prevPinned;
                  return (
                    <>
                      {showDivider && (
                        <tr key={`div-${p.id}`}>
                          <td colSpan={7} className="px-4 py-1 bg-gray-50 border-y border-gray-100">
                            <span className="text-xs text-gray-400">Other projects</span>
                          </td>
                        </tr>
                      )}
                      <tr key={p.id}
                        onClick={() => navigate(`/projects/${p.id}`)}
                        className="hover:bg-bh-50 cursor-pointer transition-colors group">
                        <td className="pl-3 py-3 text-center">
                          <PinButton id={p.id} className="mx-auto" />
                        </td>
                        <td className="px-4 py-3">
                          <Badge color={colorFor(p.machine_type_id)}>{labelFor(p.machine_type_id)}</Badge>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{p.name}</td>
                        <td className="px-4 py-3 text-gray-500 hidden md:table-cell max-w-xs truncate">
                          {p.description || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">
                          {p.owner_full_name || p.owner_username || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                          {new Date(p.updated_at).toLocaleDateString()}
                        </td>
                        <td className="px-2 py-3 text-center">
                          {!isSearching && p.owner_id === user?.id && (
                            <button onClick={e => handleDelete(e, p.id)}
                              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-red-400 transition-all">
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Project">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input label="Project name" value={form.name} required
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="My Project" />
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase tracking-wide block mb-1">
              Machine type
            </label>
            <select value={form.machine_type_id ?? ""}
              onChange={e => setForm(f => ({ ...f, machine_type_id: parseInt(e.target.value) || null }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-bh-500">
              <option value="">— Not set —</option>
              {machineTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <Input label="Description (optional)" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Short description…" />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button type="submit" loading={creating}>Create</Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
